import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { format } from 'date-fns';
import MUIAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import MUIAccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { styled } from '@mui/material/styles';

const accordionDetailsStyles = {
  columnGap: 3,
  display: 'grid',
  gridRowGap: 16,
  gridTemplateColumns: '1fr 1fr',
};

const orDashes = R.or(R.__, '--');
const propOrDashes = R.propOr('--');

const Accordion = styled(MUIAccordion)(({ theme }) => ({
  boxShadow: theme.shadows[6],
}));

const AccordionSummary = styled(MUIAccordionSummary)(({ theme }) => ({
  boxShadow: theme.shadows[2],
}));

const BatchDetailsAccordion = ({ batchData, batchState }) => {
  const {
    availableDate,
    comments,
    component,
    batchOwner,
    distillateFuelType,
    fuelType,
    oxygenate,
    origin,
    po,
    product,
    rvp,
    shipments,
    sulphurContent,
  } = batchState;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const [batchDetailsExpanded, setBatchDetailsExpanded] = useState(!isMobile);
  const handleBatchDetailsChange = (_, isExpanded) => {
    setBatchDetailsExpanded(isExpanded);
  };

  // parseVolume :: String -> Number
  const parseVolume = R.compose(
    R.ifElse(R.isEmpty, R.always(0), x => parseInt(x)),
    R.replace(/\D/g, '')
  );

  // volumeReducer :: ((Number, Shipment)
  //               -> Number) -> Number
  //               -> [Shipment] -> Number
  const volumeReducer = (acc, { volume }) => R.add(acc, parseVolume(volume));

  // totalVolume :: [Shipment] -> Number
  const totalVolume = shipments.reduce(volumeReducer, 0);

  const volumeLabel = (
    <Stack alignItems="center" direction="row" spacing={1}>
      <Typography variant="caption">Volume</Typography>
      <Tooltip
        enterTouchDelay={10}
        title="Total Batch Volume equals the sum of all associated shipment volumes"
      >
        <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />
      </Tooltip>
    </Stack>
  );

  return (
    <Box>
      <Accordion expanded={batchDetailsExpanded} onChange={handleBatchDetailsChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
          <Typography>Batch Details</Typography>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          <Box>
            {R.isNil(batchData) && (
              <>
                <AccordionItem label="Batch Owner" value={batchOwner.customerNameLong} />
                <AccordionItem label="Origin" value={origin.originName} />
              </>
            )}
            {!R.isNil(batchData) && (
              <>
                <AccordionItem label="Batch ID" value={R.prop('batchId', batchData)} />
                <AccordionItem label="Batch Owner" value={batchOwner.customerNameLong} />
                <AccordionItem label="Origin" value={origin.originName} />
                <AccordionItem label="Access Code" value={R.prop('accessCode', batchData)} />
              </>
            )}
            <AccordionItem label="Available Date" value={format(new Date(availableDate), 'P')} />
            {R.isNil(batchData) && <AccordionItem label="Purchase Order" value={orDashes(po)} />}
            {!R.isNil(batchData) && <AccordionItem label="Purchase Order" value={orDashes(po)} />}
            <AccordionItem label="Comment" value={orDashes(comments)} />
            <AccordionItem
              label={volumeLabel}
              value={`${totalVolume.toLocaleString('en-US')} bbl`}
            />
          </Box>
          <Box>
            <AccordionItem label="Product" value={product.productName} />
            <AccordionItem
              label="Fuel Type"
              value={propOrDashes('display', fuelType ?? distillateFuelType)}
            />
            {product.isGasoline && (
              <>
                <AccordionItem label="Oxygenate Amount" value={orDashes(oxygenate)} />
                <AccordionItem label="RVP" value={propOrDashes('display', rvp)} />
                <AccordionItem label="Component" value={propOrDashes('display', component)} />
              </>
            )}
            {!product.isGasoline && (
              <>
                <AccordionItem
                  label="Sulfur Content"
                  value={propOrDashes('display', sulphurContent)}
                />
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

BatchDetailsAccordion.propTypes = {
  batchData: PropTypes.shape({}).isRequired,
  batchState: PropTypes.shape({
    availableDate: PropTypes.string,
    batchOwner: PropTypes.shape({
      customerNameLong: PropTypes.string,
    }),
    comments: PropTypes.string,
    component: PropTypes.string,
    distillateFuelType: PropTypes.string,
    fuelType: PropTypes.shape({}),
    origin: PropTypes.shape({
      originName: PropTypes.string,
    }),
    oxygenate: PropTypes.string,
    po: PropTypes.string,
    product: PropTypes.shape({
      isGasoline: PropTypes.bool,
      productName: PropTypes.string,
    }),
    rvp: PropTypes.shape({}),
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
    sulphurContent: PropTypes.string,
  }).isRequired,
};

const AccordionItem = ({ label, value }) => (
  <Stack spacing={1} sx={{ my: 1.5 }}>
    <Typography variant="caption">{label}</Typography>
    <Typography sx={{ overflowWrap: 'anywhere' }} variant="subtitle2">
      {value}
    </Typography>
  </Stack>
);

AccordionItem.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
};

export default BatchDetailsAccordion;
